/**
 * Ludipek add-to-cart enhancer (ES module).
 *
 * Progressively enhances our custom `[data-lp-add-form]` add-to-cart forms (PDP + product cards) so adding a
 * product does an AJAX add and opens the native cart drawer instead of the plain form POST that Shopify
 * 302-redirects to /cart. It reuses Horizon's native cart contract: POST /cart/add with the section ids of
 * every <cart-items-component> on the page, then dispatch a CartAddEvent carrying the rendered `sections` —
 * which makes <cart-items-component> morph its section and <cart-drawer-component auto-open> slide open.
 *
 * B2B: the fetch to /cart/add trips b2b-cart-snapshot.js's window.fetch monkeypatch, so the cart is re-signed
 * exactly as on a native add. b2b-pricing.js re-overlays the drawer prices on the resulting 'cart:update'.
 *
 * No-JS fallback: the plain <form action="/cart/add"> still works (navigates to /cart).
 */
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

// `Theme` is a global LEXICAL const declared in snippets/scripts.liquid (a classic <script>), so it is NOT
// exposed on `window` — read it bare, the way product-form.js does. Its cart_add_url already carries the
// '.js' suffix that makes Shopify ALWAYS return JSON; a bare '/cart/add' can 302-redirect to /cart, which
// makes response.json() throw and the add silently fail (the original bug).
const CART_ADD_URL = (typeof Theme !== 'undefined' && Theme.routes?.cart_add_url) || '/cart/add.js';

/** Section ids of every cart-items-component on the page (the cart surfaces that must re-render). */
function sectionIdsToRender() {
  return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
    .map((element) => element.dataset.sectionId)
    .filter(Boolean);
}

/** AJAX-add the given form's line, then dispatch CartAddEvent so the drawer re-renders + auto-opens. */
async function addToCart(form) {
  const formData = new FormData(form);
  const sectionIds = sectionIdsToRender();
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const config = fetchConfig('javascript', { body: formData });
  const response = await fetch(CART_ADD_URL, {
    ...config,
    headers: { ...config.headers, Accept: 'text/html' },
  });
  const payload = await response.json();

  if (payload.status) {
    // Shopify add error (e.g. out of stock); surface minimally and let the buyer retry.
    console.warn('[ludipek-add-to-cart] add failed:', payload.message || payload.description || payload.status);
    return;
  }

  document.dispatchEvent(
    new CartAddEvent({}, String(formData.get('id') || ''), {
      source: 'ludipek-add-to-cart',
      itemCount: Number(formData.get('quantity')) || 1,
      sections: payload.sections,
    })
  );
}

document.addEventListener('submit', (event) => {
  const form = event.target instanceof Element ? event.target.closest('[data-lp-add-form]') : null;
  if (!form) return;
  event.preventDefault();
  addToCart(form).catch((error) => console.error('[ludipek-add-to-cart]', error));
});

// Keep the SSR-only header cart badge in sync after any AJAX cart change (add / drawer qty change / remove).
// CartAddEvent.eventName === 'cart:update', which Horizon fires for both adds and updates.
let badgeTimer;
document.addEventListener(CartAddEvent.eventName, () => {
  clearTimeout(badgeTimer);
  badgeTimer = setTimeout(() => {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        const count = cart.item_count || 0;
        document.querySelectorAll('[data-lp-cart-count]').forEach((badge) => {
          badge.textContent = count > 99 ? '99+' : String(count);
          badge.toggleAttribute('hidden', count === 0);
        });
      })
      .catch(() => {});
  }, 150);
});
